import React, { useState, useEffect } from 'react';
import { useCart } from '../CartContext';
import { dailyDeals, dealBannerBg } from '../data';

function useCountdown(target) {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const calc = () => {
      const diff = new Date(target) - new Date();
      if (diff <= 0) return setTimeLeft({ d: 0, h: 0, m: 0, s: 0 });
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [target]);
  return timeLeft;
}

function CountdownDisplay({ target }) {
  const { d, h, m, s } = useCountdown(target);
  const pad = n => String(n ?? 0).padStart(2, '0');
  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
      {[['D', d], ['H', h], ['M', m], ['S', s]].map(([lbl, val], i, arr) => (
        <React.Fragment key={lbl}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#f0fdf4', borderRadius: '5px', padding: '3px 6px', minWidth: 34 }}>
            <span style={{ fontWeight: 800, fontSize: '0.8rem', color: '#16a34a', lineHeight: 1 }}>{pad(val)}</span>
            <span style={{ fontSize: '0.55rem', color: '#6b7280', textTransform: 'uppercase' }}>{lbl}</span>
          </div>
          {i < arr.length - 1 && <span style={{ color: '#9ca3af', fontWeight: 800, fontSize: '0.8rem' }}>:</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function DealCard({ product }) {
  const { addToCart } = useCart();
  const [qty, setQty]   = useState(1);
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    addToCart({ ...product, image: product.image, isB2B: false }, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  const price    = parseFloat(product.price);
  const orig     = product.originalPrice ? parseFloat(product.originalPrice) : null;
  const discount = orig ? Math.round(((orig - price) / orig) * 100) : null;

  return (
    <div style={{
      border: '1px solid #e5e7eb', borderRadius: '10px', background: '#fff',
      overflow: 'hidden', display: 'flex', flexDirection: 'column',
      position: 'relative', transition: 'box-shadow 0.2s, transform 0.2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Added overlay */}
      {added && (
        <div style={{ position: 'absolute', bottom: '8px', left: '8px', right: '8px', background: '#16a34a', color: 'white', borderRadius: '6px', padding: '5px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'center', zIndex: 10, boxShadow: '0 2px 8px rgba(22,163,74,0.4)' }}>
          ✓ Added to cart
        </div>
      )}

      {/* Discount badge */}
      {discount && (
        <div style={{ position: 'absolute', top: '8px', left: '8px', background: '#dc2626', color: 'white', padding: '2px 7px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: 700, zIndex: 1 }}>
          {discount}% OFF
        </div>
      )}

      {/* Image */}
      <div style={{ background: '#f8fafc', height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
        <img src={product.image} alt={product.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => e.target.style.display = 'none'} />
      </div>

      {/* Info */}
      <div style={{ padding: '10px 10px 12px', flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span style={{ fontSize: '0.68rem', color: '#16a34a', fontWeight: 600 }}>{product.category}</span>
        <p style={{ fontSize: '0.82rem', fontWeight: 700, color: '#111827', lineHeight: 1.3, margin: 0, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {product.name}
        </p>

        
        {/* Price */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#16a34a' }}>₹{price}{product.unit ? `/${product.unit}` : ''}</span>
          {orig && <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '0.75rem' }}>₹{orig}</span>}
        </div>

        {/* Qty + Add to Cart */}
        <div style={{ marginTop: 'auto', paddingTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 }}>
            <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 26, height: 28, background: '#f9fafb', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#374151' }}>−</button>
            <span style={{ minWidth: '22px', textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>{qty}</span>
            <button onClick={() => setQty(q => q + 1)} style={{ width: 26, height: 28, background: '#f9fafb', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#374151' }}>+</button>
          </div>
          <button
            onClick={handleAdd}
            style={{ padding: '6px 14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', transition: 'background 0.15s', whiteSpace: 'nowrap' }}
            onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
            onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}
          >
             + Add
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DailyDeals() {
  return (
    <section style={{ margin: '2.5rem 0' }}>
      <div className="container">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', marginBottom: '1.25rem' }}>Daily Best Sells</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px' }}>
          {/* Promo banner tile */}
          <div style={{
            backgroundImage: `url(${dealBannerBg})`, backgroundSize: 'cover', backgroundPosition: 'center',
            borderRadius: '10px', padding: '1.5rem 1.25rem', minHeight: 280,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
            position: 'relative', overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.42)', borderRadius: 10 }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <span style={{ fontSize: '0.7rem', color: '#bbf7d0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Daily Deal</span>
              <h3 style={{ fontSize: '1rem', color: 'white', fontWeight: 700, marginTop: '6px', marginBottom: '8px', lineHeight: 1.3 }}>100% Organic Coffee Beans</h3>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem', marginBottom: '1rem' }}>Best deal before it ends!</p>
            </div>
          </div>

          {dailyDeals.map(product => (
            <DealCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}
